"use client";

import { TypeFooter } from "@contentful/types";
import { faFacebook } from "@fortawesome/free-brands-svg-icons/faFacebook";
import { faHouzz } from "@fortawesome/free-brands-svg-icons/faHouzz";
import { faInstagram } from "@fortawesome/free-brands-svg-icons/faInstagram";
import { faLinkedin } from "@fortawesome/free-brands-svg-icons/faLinkedin";
import { faPinterestSquare } from "@fortawesome/free-brands-svg-icons/faPinterestSquare";
import { faTiktok } from "@fortawesome/free-brands-svg-icons/faTiktok";
import { faTwitter } from "@fortawesome/free-brands-svg-icons/faTwitter";
import { faEnvelope } from "@fortawesome/pro-light-svg-icons/faEnvelope";
import { faPhone } from "@fortawesome/pro-light-svg-icons/faPhone";
import {
  FontAwesomeIcon,
  FontAwesomeIconProps,
} from "@fortawesome/react-fontawesome";
import { SegmentFlows } from "@yardzen/next-client-util";
import cookies from "js-cookie";
import { useEffect, useState } from "react";
import { Link } from "./link";
import { Select, SelectItem } from "./select";
import { Small } from "./small";

function getSocialIcon(icon: string | undefined) {
  if (icon === "faInstagram") {
    return faInstagram;
  } else if (icon === "faFacebook") {
    return faFacebook;
  } else if (icon === "faPinterestSquare") {
    return faPinterestSquare;
  } else if (icon === "faTwitter") {
    return faTwitter;
  } else if (icon === "faLinkedin") {
    return faLinkedin;
  } else if (icon === "faHouzz") {
    return faHouzz;
  } else if (icon === "faTiktok") {
    return faTiktok;
  }
}

export function Footer({
  footer,
}: {
  footer: TypeFooter<"WITHOUT_UNRESOLVABLE_LINKS">;
}) {
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const promoCookie = cookies.get("yzpdc");
  const isPromoCookieHidden = cookies.get("yzhpb");
  const links = footer?.fields.links;
  const socialLinks = footer?.fields.socialLinks;
  const legalLinks = footer?.fields.legalLinks;
  const disclaimerText = footer?.fields.disclaimer;
  const promoDisclaimerText = footer?.fields.promoDisclaimer;
  const leftLinks = links?.slice(0, 7);
  const rightLinks = links?.slice(7);

  return (
    <div className="flex w-full flex-col bg-[#272727] px-8 pb-4 pt-14 text-white">
      <div className="flex w-full flex-col md:h-32 md:flex-row">
        <div className="flex w-full flex-col items-center pb-4 md:hidden">
          <YZLogo />
        </div>
        <div className="flex flex-1 flex-col flex-wrap md:flex-col">
          {leftLinks?.map((link) => (
            <div
              className="flex items-center justify-center py-1 md:w-1/2 md:justify-start"
              key={link?.fields.title}
            >
              {link?.fields.title !== "Trending Cities" ? (
                <Link
                  key={link?.fields.title}
                  href={link?.fields.url}
                  tracking={{
                    button_content: link?.fields.title,
                    flow_name: SegmentFlows.FOOTER,
                    button_name: "Footer Link - " + link?.fields.title,
                  }}
                >
                  {link?.fields.title}
                </Link>
              ) : (
                <Select
                  onChange={(e) => {
                    if (e.target.value) {
                      window.location.href = `https://yardzen.com/${e.target.value}`;
                    }
                  }}
                  className="!m-0 !h-auto w-[150px] rounded-none border-0 bg-transparent !p-0 text-base font-normal focus:outline-none focus:ring-0"
                >
                  {trendingCities.map((city) => (
                    <SelectItem
                      key={city.href}
                      value={city.href}
                      tracking={{
                        field_name: "Trending Cities",
                        form_name: "Trending Cities Footer Select",
                        flow_name: SegmentFlows.FOOTER,
                        text_value: city.title,
                      }}
                    >
                      {city.title}
                    </SelectItem>
                  ))}
                </Select>
              )}
            </div>
          ))}
        </div>
        <div className="hidden flex-1 items-center justify-center md:flex">
          {<YZLogo />}
        </div>
        <div className="flex flex-1 flex-col flex-wrap items-center text-center md:flex-row-reverse md:items-end md:text-end">
          {rightLinks?.map((link) => (
            <Link
              key={link?.fields.title}
              href={link?.fields.url}
              className="w-1/2 py-1"
              tracking={{
                button_content: link?.fields.title,
                flow_name: SegmentFlows.FOOTER,
                button_name: link?.fields.title || "",
              }}
            >
              {link?.fields.title}
            </Link>
          ))}
        </div>
      </div>
      <hr className="border-brand-brown-black my-4" />
      <div className="flex w-full flex-col items-center md:flex-row">
        <div className="flex flex-1 flex-col gap-2">
          <Link
            href="mailto:hello@yardzen.com"
            variant="default"
            className="mb-2"
            tracking={{
              button_content: "hello@yardzen.com",
              flow_name: SegmentFlows.FOOTER,
              button_name: "Yardzen Email",
            }}
          >
            <FontAwesomeIcon icon={faEnvelope} className="mr-2 h-4 w-4" />
            hello@yardzen.com
          </Link>

          <Link
            href="tel:8889273936"
            variant="default"
            tracking={{
              button_content: "(888) 927-3936",
              flow_name: SegmentFlows.FOOTER,
              button_name: "Yardzen Phone Number",
            }}
          >
            <FontAwesomeIcon icon={faPhone} className="mr-2 h-4 w-4" />
            (888) 927-3936
          </Link>
        </div>
        <div className="max-w-1/3 grid flex-1 grid-flow-col items-center justify-center gap-4 py-4">
          {socialLinks?.map((link) => (
            <SocialLink
              key={link?.fields.url}
              href={link?.fields.url}
              icon={getSocialIcon(link?.fields.icon)}
              tracking={{
                button_content: link?.fields.url || "",
                flow_name: SegmentFlows.FOOTER,
                button_name: "Social Link - " + link?.fields.title,
              }}
            />
          ))}
        </div>
        <div className="flex flex-1 justify-end">{<BBBLogo />}</div>
      </div>
      <hr className="border-brand-brown-black my-4" />

      <Small className="flex w-full flex-row flex-nowrap items-center justify-center gap-1 py-2">
        {disclaimerText}
        {legalLinks?.map((link, index) => (
          <Link
            key={link?.fields.title}
            href={link?.fields.url}
            tracking={{
              button_content: link?.fields.title,
              flow_name: SegmentFlows.FOOTER,
              button_name: "Legal Link - " + link?.fields.title,
            }}
          >
            {"| "}
            {link?.fields.title}
          </Link>
        ))}
      </Small>

      {isClient && promoCookie && !isPromoCookieHidden && (
        <div className="flex items-center justify-center pt-2 sm:px-12">
          <Small className="text-center">
            *Limited to new packages purchased 7 days after signup. Must enter
            unique redemption code at checkout to receive a free expedited
            service upgrade. Cannot be combined with other promotions. Not valid
            on Premium, My Lowe's Rewards, Starter Packages, Botanical Packages,
            or gift cards.
          </Small>
        </div>
      )}
      {promoDisclaimerText && (
        <div className="flex items-center justify-center pt-2">
          <Small className="text-center">{promoDisclaimerText}</Small>
        </div>
      )}
    </div>
  );
}

function SocialLink({
  href,
  icon,
  tracking,
}: {
  href: string | undefined;
  icon: FontAwesomeIconProps["icon"] | undefined;
  tracking: {
    button_content: string;
    flow_name: string;
    button_name: string;
  };
}) {
  return (
    <Link href={href} target="_blank" tracking={tracking}>
      {icon && (
        <FontAwesomeIcon icon={icon} className="h-4 w-4 md:h-6 md:w-6" />
      )}
    </Link>
  );
}

function YZLogo() {
  return (
    <svg
      id="a"
      width="69"
      height="85"
      data-name="Layer 1"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 66.95 83"
    >
      <path
        d="M66.57,29.5l-.3-3.9c-.9-12.6-10.2-22.8-22.7-24.7l-1.3-.2c-2.9-.4-5.9-.7-8.8-.7s-5.9.2-8.8.7l-1.3.2C10.87,2.8,1.57,12.9.67,25.6l-.3,3.9c-.5,7.7-.5,15.5,0,23.2l.3,4.7c.8,12.1,9.8,22.2,21.7,24.5,3.7.7,7.4,1.1,11.1,1.1s7.4-.4,11.1-1.1c11.9-2.3,20.8-12.4,21.7-24.5l.3-4.7c.5-7.7.5-15.5,0-23.2ZM64.07,52.5l-.3,4.7c-.8,11-8.8,20.1-19.6,22.2-3.5.7-7.1,1-10.6,1s-7.1-.3-10.6-1c-10.8-2.1-18.9-11.2-19.6-22.2l-.3-4.7c-.5-7.6-.5-15.3,0-22.9l.3-3.9C3.97,14.3,12.47,5.1,23.77,3.4l1.3-.2c2.8-.4,5.6-.6,8.4-.6h0c2.8,0,5.6.2,8.4.6l1.3.2c11.3,1.7,19.8,10.9,20.6,22.4l.3,3.9c.5,7.6.5,15.3,0,22.9v-.1Z"
        style={{ fill: "white" }}
      />
      <polygon
        points="34.18 43.8 22.37 22.7 16.77 22.7 30.87 47.8 30.87 65.5 35.97 65.5 35.97 47.5 49.88 22.7 45.88 22.7 34.18 43.8"
        style={{ fill: "white" }}
      />
    </svg>
  );
}

function BBBLogo() {
  return (
    <svg
      id="a"
      data-name="katman 1"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 402.01 150.98"
      width="150"
    >
      <path
        d="M238.61,55.36c0-1.8-1.38-2.91-3.22-2.91h-4.81v5.81h4.81c1.84,0,3.22-1.09,3.22-2.91Z"
        style={{ fill: "#FFFFFF" }}
      />
      <path
        d="M82.99,139.59h13.49c5.74,0,11.86-2.52,10.85-9.15-.58-3.8-2.09-4.69-5.13-6.05,2.31-1.36,3.74-3.33,3.74-6.06,0-5.44-4.46-7.67-9.34-7.67h-13.6v28.93ZM97.02,134.11h-7.89v-6.59h7.89c5.79,0,5.44,6.59,0,6.59ZM89.15,116.18h7.03c4.42,0,4.57,6.16.27,6.16h-7.34l.04-6.16Z"
        style={{ fill: "#FFFFFF" }}
      />
      <path
        d="M117.75,139.3c3.66,0,6.67-3,6.67-6.67v-.06c0-3.64-3-6.65-6.67-6.65s-6.67,3-6.67,6.67v.06c.02,3.66,3.02,6.67,6.67,6.65ZM111.69,132.6c0-3.33,2.73-6.07,6.06-6.07s6.05,2.73,6.06,6.05v.06c0,3.33-2.73,6.06-6.06,6.06s-6.05-2.73-6.06-6.05v-.06Z"
        style={{ fill: "#FFFFFF" }}
      />
      <path
        d="M61.64,47.43l16.32,11.97c3.31,2.42,3.74,8.62,1.34,11.78l1.38,1.05,11.26-15.83c5.06-7.11,4.77-17.63-3.18-23.39l-17.46-12.69c-2.44-1.76-3.66-4.79-1.63-7.67l-1.49-1.07-9.92,13.74c-4.65,6.43-2.96,17.42,3.39,22.11Z"
        style={{ fill: "#FFFFFF" }}
      />
      <polygon
        points="57.51 105.7 79.02 105.7 81.36 97.72 94.52 97.72 92.8 91.85 43.73 91.85 42.01 97.72 55.17 97.72 57.51 105.7"
        style={{ fill: "#FFFFFF" }}
      />
      <path
        d="M53.71,74.56l11.82,8.53c2.03,1.47,2.19,2.93,1.22,4.46l1.72,1.28,5.99-8.29c3.7-5.13,3.37-12.09-2.11-16.1l-11.94-8.7c-1.41-1.05-1.57-2.5-.79-4.11l-1.69-1.2-6.38,8.8c-3.2,4.4-2.21,12.21,2.15,15.35Z"
        style={{ fill: "#FFFFFF" }}
      />
      <path
        d="M53.31,130.43c-.58-3.8-2.07-4.69-5.13-6.05,2.31-1.36,3.72-3.33,3.72-6.05,0-5.44-4.46-7.67-9.34-7.67h-13.6v28.91h13.49c5.75,0,11.86-2.52,10.87-9.15ZM35.09,116.18h7.09c4.42,0,4.57,6.16.27,6.16h-7.36v-6.16ZM35.09,127.52h7.91c5.79,0,5.44,6.59,0,6.59h-7.91v-6.59Z"
        style={{ fill: "#FFFFFF" }}
      />
      <polygon
        points="164.51 62.28 171.21 62.28 167.86 53.02 164.51 62.28"
        style={{ fill: "#FFFFFF" }}
      />
      <path
        d="M281.22,59.22h.04c0-3.8-2.34-6.74-6.74-6.74h-3.89v13.45h3.89c4.22,0,6.7-3.06,6.7-6.7Z"
        style={{ fill: "#FFFFFF" }}
      />
      <path
        d="M116.16,135.85v-2.58h1.74l1.98,2.58h1.26l-2.11-2.75c1.03-.25,1.76-.91,1.76-2.02v-.04c0-.48-.19-.97-.56-1.32-.56-.48-1.3-.76-2.03-.72h-3.06l.02,6.84h1.01ZM116.14,129.92h2.02c.99,0,1.57.47,1.57,1.18v.04c0,.78-.68,1.22-1.63,1.22h-1.96v-2.44Z"
        style={{ fill: "#FFFFFF" }}
      />
      <path
        d="M344.99,52.47h-3.89v13.45h3.89c4.22,0,6.7-3.06,6.7-6.7h.04c0-3.8-2.34-6.74-6.74-6.74Z"
        style={{ fill: "#FFFFFF" }}
      />
      <path
        d="M382.19,0H19.82C8.95.02.02,8.95,0,19.82v111.34c.02,10.87,8.95,19.8,19.82,19.82v-.02h362.37c10.87-.02,19.8-8.95,19.82-19.82V19.82c-.02-10.87-8.95-19.8-19.82-19.82ZM266.01,48.44h8.49c6.74,0,11.41,4.28,11.41,10.77s-4.69,10.73-11.41,10.73h-8.49v-21.51ZM246.98,48.44h15.25v4.03h-10.68v4.55h10.42v4.03h-10.42v4.86h10.64v4.03h-15.21v-21.51ZM136.28,147.87H19.82c-9.17,0-16.7-7.56-16.72-16.72V19.82C3.1,10.66,10.66,3.12,19.82,3.1h116.45v144.76ZM170.61,106.88l-11.66.02v-21.53h11.32c4.11,0,6.2,2.62,6.2,5.48,0,2.71-1.69,4.51-3.7,4.94,2.29.35,4.11,2.58,4.11,5.27,0,3.22-2.15,5.81-6.26,5.81ZM173.79,69.95l-1.32-3.64h-9.22l-1.36,3.64h-5.23l8.33-21.51h5.74l8.29,21.51h-5.23ZM200.01,98.28c0,5.35-3.16,8.99-9.71,8.99-6.53.02-9.77-3.66-9.77-8.95v-12.94h4.69v12.77c0,3,1.76,5.06,5.06,5.06s5.04-2.07,5.04-5.06v-12.77h4.69v12.91ZM191.68,66.19c2.13-.02,4.09-1.22,5.06-3.12l3.91,1.96c-1.55,2.67-4.26,5.27-8.99,5.27-6.43.02-11.45-4.48-11.45-11.1s5-11.14,11.45-11.14c3.76-.1,7.25,1.96,8.99,5.27l-3.93,1.94c-.97-1.88-2.91-3.1-5.04-3.14-3.89,0-6.74,3-6.74,7.03s2.83,7.03,6.74,7.03ZM212.06,107.29c-4.11-.04-7.07-1.4-9.09-3.43l2.48-3.49c1.78,1.86,4.26,2.91,6.84,2.91s3.74-1.16,3.74-2.38c0-1.57-2-2.03-4.36-2.62-3.53-.79-8.02-1.76-8.02-6.67,0-3.58,3.1-6.55,8.16-6.55,3.43,0,6.26,1.03,8.39,3l-2.56,3.35c-1.72-1.53-3.95-2.36-6.26-2.34-1.96,0-3.06.87-3.06,2.15,0,1.4,1.96,1.84,4.28,2.38,3.53.83,8.06,1.9,8.06,6.78,0,3.93-2.81,6.9-8.6,6.9ZM214.04,66.19c2.13-.02,4.09-1.22,5.06-3.12l3.93,1.9c-1.55,2.67-4.26,5.27-8.99,5.27-6.43.08-11.45-4.42-11.45-11.04s5-11.14,11.45-11.14c3.76-.1,7.25,1.96,8.99,5.27l-3.93,1.94c-.99-1.9-2.93-3.1-5.06-3.14-3.89,0-6.74,3-6.74,7.03s2.83,7.03,6.74,7.03ZM228.59,106.88h-4.57v-21.51h4.57v21.51ZM230.58,69.95h-4.57v-21.51h10.06c4.48,0,7.23,2.93,7.23,6.94,0,3.8-2.38,5.87-4.69,6.39l4.84,8.22-5.29-.04-4.22-7.62h-3.35v7.62ZM252.33,106.88h-4.42v.02l-10.25-14.03v14.03h-4.57v-21.53h4.69l9.98,13.49v-13.49h4.57v21.51ZM272.05,89.41h-10.66v4.55h10.42v4.03h-10.42v4.86h10.64v4.03l-15.23.02v-21.53h15.25v4.03ZM283.5,107.29c-4.15-.04-7.11-1.4-9.13-3.43l2.48-3.49c1.78,1.86,4.26,2.91,6.84,2.91s3.74-1.16,3.74-2.38c0-1.57-2-2.03-4.34-2.62-3.53-.79-8.02-1.76-8.02-6.67,0-3.58,3.1-6.55,8.16-6.55,3.41,0,6.26,1.03,8.39,3l-2.54,3.35c-1.72-1.53-3.95-2.36-6.26-2.34-1.96,0-3.06.87-3.06,2.15,0,1.4,1.98,1.84,4.28,2.38,3.53.83,8.06,1.9,8.06,6.78,0,3.93-2.81,6.9-8.6,6.9ZM293.97,69.95h-4.57v-21.51h4.57v21.51ZM303.07,107.29c-4.13-.04-7.11-1.4-9.09-3.43l2.48-3.49c1.78,1.86,4.26,2.91,6.84,2.91s3.74-1.16,3.74-2.38c0-1.57-2-2.03-4.36-2.62-3.53-.79-8.02-1.76-8.02-6.67,0-3.58,3.1-6.55,8.16-6.55,3.43,0,6.26,1.03,8.39,3l-2.56,3.35c-1.72-1.53-3.95-2.36-6.26-2.34-1.96,0-3.06.87-3.06,2.15,0,1.4,1.98,1.84,4.28,2.38,3.53.83,8.06,1.9,8.06,6.78,0,3.93-2.81,6.9-8.6,6.9ZM314.29,52.47h-6.26v17.48h-4.61v-17.48h-6.3v-4.03h17.17v4.03ZM332.7,52.47h-10.68v4.55h10.41v4.03h-10.41v4.86h10.64v4.03h-15.21v-21.51h15.25v4.03ZM344.97,69.95h-8.49v-21.51h8.49c6.74,0,11.41,4.28,11.41,10.77s-4.69,10.73-11.41,10.73Z"
        style={{ fill: "#FFFFFF" }}
      />
      <path
        d="M75.18,124.38c2.31-1.36,3.72-3.33,3.72-6.05,0-5.44-4.46-7.67-9.34-7.67h-13.6v28.91h13.49c5.75,0,11.86-2.52,10.87-9.15-.58-3.8-2.07-4.69-5.13-6.05ZM62.14,116.18h7.07c4.42,0,4.57,6.16.27,6.16h-7.36l.02-6.16ZM70.01,134.11h-7.89v-6.59h7.89c5.79,0,5.44,6.59,0,6.59Z"
        style={{ fill: "#FFFFFF" }}
      />
      <path
        d="M171.8,91.67v-.02c0-1.3-1.07-2.34-2.34-2.34h-5.91v4.69h5.74c1.55,0,2.52-.97,2.52-2.33Z"
        style={{ fill: "#FFFFFF" }}
      />
      <path
        d="M169.43,97.93h-5.89v5h5.89c1.72,0,2.77-.95,2.77-2.48,0-1.36-.97-2.52-2.77-2.52Z"
        style={{ fill: "#FFFFFF" }}
      />
    </svg>
  );
}

const trendingCities: { href: string; title: string }[] = [
  {
    href: "#",
    title: "Trending Cities",
  },
  {
    href: "/phoenix-landscape-design",
    title: "Phoenix, AZ Landscape Design",
  },
  {
    href: "/tucson-landscape-design",
    title: "Tucson, AZ Landscape Design",
  },
  {
    href: "/huntington-beach-landscape-design",
    title: "Huntington Beach, CA Landscape Design",
  },
  {
    href: "/los-angeles-landscape-design",
    title: "Los Angeles, CA Landscape Design",
  },
  {
    href: "/mill-valley-landscape-design",
    title: "Mill Valley, CA Landscape Design",
  },
  {
    href: "/napa-landscape-design",
    title: "Napa Valley, CA Landscape Design",
  },
  {
    href: "/oakland-landscape",
    title: "Oakland, CA Landscape Design",
  },
  {
    href: "/palo-alto-landscape-design",
    title: "Palo Alto, CA Landscape Design",
  },
  {
    href: "/sacramento-landscape-design",
    title: "Sacramento, CA Landscape Design",
  },
  {
    href: "/san-diego-landscape-design",
    title: "San Diego, CA Landscape Design",
  },
  {
    href: "/san-jose-landscape-design",
    title: "San Jose, CA Landscape Design",
  },
  {
    href: "/landscape-design-in-san-francisco-ca",
    title: "San Francisco, CA Landscape Design",
  },
  {
    href: "/thousand-oaks-landscape-design",
    title: "Thousand Oaks, CA Landscape Design",
  },
  {
    href: "/ventura-landscape-design",
    title: "Ventura, CA Landscape Design",
  },
  {
    href: "/aurora-landscape-design",
    title: "Aurora, CO Landscape Design",
  },
  {
    href: "/denver-landscape-design",
    title: "Denver, CO Landscape Design",
  },
  {
    href: "/lakewood-landscape-design",
    title: "Lakewood, CO Landscape Design",
  },
  {
    href: "/bridgeport-landscape-design",
    title: "Bridgeport, CT Landscape Design",
  },
  {
    href: "/greenwich-landscape-design",
    title: "Greenwich, CT Landscape Design",
  },
  {
    href: "/new-canaan-landscape-design",
    title: "New Canaan, CT Landscape Design",
  },
  {
    href: "/stamford-landscape-design",
    title: "Stamford, CT Landscape Design",
  },
  {
    href: "/landscape-design-in-washington-dc",
    title: "Washington, DC Landscape Design",
  },
  {
    href: "/miami-landscape-design",
    title: "Miami, FL Landscape Design",
  },
  {
    href: "/orlando-landscape-design",
    title: "Orlando, FL Landscape Design",
  },
  {
    href: "/palm-beach-landscape-design",
    title: "Palm Beach, FL Landscape Design",
  },
  {
    href: "/tampa-landscape-design",
    title: "Tampa, FL Landscape Design",
  },
  {
    href: "/winter-park-landscape-design",
    title: "Winter Park, FL Landscape Design",
  },
  {
    href: "/atlanta-landscape-design",
    title: "Atlanta, GA Landscape Design",
  },
  {
    href: "/landscape-design-in-roswell-ga",
    title: "Roswell, GA Landscape Design",
  },
  {
    href: "/landscape-design-in-twin-falls",
    title: "Twin Falls, ID Landscape Design",
  },
  {
    href: "/landscape-design-in-joliet",
    title: "Joliet, IL Landscape Design",
  },
  {
    href: "/landscape-design-boston",
    title: "Boston, MA Landscape Design",
  },
  {
    href: "/landscape-design-in-cambridge",
    title: "Cambridge, MA Landscape Design",
  },
  {
    href: "/potomac-landscape-design",
    title: "Potomac, MD Landscape Design",
  },
  {
    href: "/landscape-design-in-portland-me",
    title: "Portland, ME Landscape Design",
  },
  {
    href: "/landscape-design-in-st-charles",
    title: "St. Charles, MO Landscape Design",
  },
  {
    href: "/landscape-design-in-burlington-nc",
    title: "Burlington, NC Landscape Design",
  },
  {
    href: "/landscape-design-in-charlotte",
    title: "Charlotte, NC Landscape Design",
  },
  {
    href: "/landscape-design-in-ocean-city",
    title: "Ocean City, NJ Landscape Design",
  },
  {
    href: "/las-vegas-landscape-design",
    title: "Las Vegas, NV Landscape Design",
  },
  {
    href: "/landscape-design-in-long-island",
    title: "Long Island, NY Landscape Design",
  },
  {
    href: "/portland-landscape-design",
    title: "Portland, OR Landscape Design",
  },
  {
    href: "/landscape-design-in-nashville",
    title: "Nashville, TN Landscape Design",
  },
  {
    href: "/landscape-design-austin",
    title: "Austin, TX Landscape Design",
  },
  {
    href: "/dallas-landscape-design",
    title: "Dallas, TX Landscape Design",
  },
  {
    href: "/houston-landscape-design",
    title: "Houston, TX Landscape Design",
  },
  {
    href: "/san-antonio-landscape-design",
    title: "San Antonio, TX Landscape Design",
  },
  {
    href: "/landscape-design-in-park-city",
    title: "Park City, UT Landscape Design",
  },
  {
    href: "/arlington-landscape-design",
    title: "Arlington, VA Landscape Design",
  },
  {
    href: "/bellevue-landscape-design",
    title: "Bellevue, WA Landscape Design",
  },
  {
    href: "/seattle-landscape-design",
    title: "Seattle, WA Landscape Design",
  },
  {
    href: "/tacoma-landscape-design",
    title: "Tacoma, WA Landscape Design",
  },
  {
    href: "/landscape-design-in-milwaukee",
    title: "Milwaukee, WI Landscape Design",
  },
  {
    href: "/landscape-design-in-cheyenne-wy",
    title: "Cheyenne, WY Landscape Design",
  },
];
